import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useCustomerById } from '../../hooks/queries';
import {
  resolveCustomerDetailBack,
  resolveCustomerEmailSearch,
} from '../../utils/customer-navigation';
import {
  buildOrderLocationState,
  customerDetailReturn,
} from '../../utils/order-navigation';
import { formatBigCommerceCalendarDate } from '../../utils/format-bigcommerce-date';
import { formatAddressType, formatCustomerAddressLines } from '../../utils/format-customer-address';

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const emailSearch = resolveCustomerEmailSearch(
    location.search,
    (location.state as { emailSearch?: string } | null)?.emailSearch,
  );
  const backNav = resolveCustomerDetailBack(location.state, emailSearch);
  const backToCustomersPath = backNav.path;
  const backToCustomersLabel = backNav.label;
  const customerId = id ? parseInt(id, 10) : 0;
  const orderReturnState = buildOrderLocationState(
    customerDetailReturn(customerId, emailSearch),
  );
  const [customerGroupName, setCustomerGroupName] = useState<string | null>(null);

  const { data: response, isLoading: loading, error: queryError } = useCustomerById(
    id ? parseInt(id) : 0,
    emailSearch,
  );
  const customer = response?.data ?? null;
  const error = queryError
    ? String((queryError as any).message ?? queryError)
    : response?.success === false
    ? response.message
    : null;

  // Resolve customer group name once the customer data arrives
  useEffect(() => {
    if (!customer) {
      setCustomerGroupName(null);
      return;
    }
    if (!customer.customer_group_id) {
      setCustomerGroupName(null);
      return;
    }

    // Check if the group is in the customer_groups array first
    const groupInArray = customer.customer_groups?.find(
      (g: any) => g.id === customer.customer_group_id
    );

    if (groupInArray && groupInArray.name) {
      setCustomerGroupName(groupInArray.name);
    } else {
      // Fetch the group separately
      apiClient.getCustomerGroupById(customer.customer_group_id).then((groupResponse) => {
        if (groupResponse.success && groupResponse.data) {
          setCustomerGroupName(groupResponse.data.name || `Group ${customer.customer_group_id}`);
        }
      }).catch((groupErr) => {
        console.error('Failed to fetch customer group:', groupErr);
        setCustomerGroupName(null);
      });
    }
  }, [customer?.id, customer?.customer_group_id]);

  const formatDate = (dateString: string) => formatBigCommerceCalendarDate(dateString);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getCustomerName = (customer: any) => {
    if (customer.first_name || customer.last_name) {
      return `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    }
    return 'N/A';
  };

  const getPromotionType = (promo: any): string => {
    if (!promo.type) {
      return 'N/A';
    }
    if (typeof promo.type === 'string') {
      if (promo.type === 'percentage_discount') {
        return 'Percentage Discount';
      }
      if (promo.type === 'fixed_discount') {
        return 'Fixed Discount';
      }
      if (promo.type === 'shipping_discount') {
        return 'Shipping Discount';
      }
      return promo.type;
    }
    return String(promo.type);
  };

  const isPromotionActive = (promo: any): boolean => {
    if (promo.status !== undefined && promo.status !== null) {
      const status = String(promo.status).toLowerCase().trim();
      return status === 'enabled' || status === 'active';
    }
    if (promo.enabled !== undefined && promo.enabled !== null) {
      return promo.enabled === true;
    }
    return false;
  };

  if (loading) {
    return (
      <div className="page-container" style={{ textAlign: 'center' }}>
        <p>Loading customer details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div
          style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            padding: '1rem',
            color: '#c33',
            marginBottom: '1rem',
          }}
        >
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={() => navigate(backToCustomersPath)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#3498db',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {backToCustomersLabel}
        </button>
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={() => navigate(backToCustomersPath)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#95a5a6',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '1rem',
          }}
        >
          ← {backToCustomersLabel}
        </button>
        <h1 style={{ margin: 0 }}>{getCustomerName(customer) || `Customer #${customer.id}`}</h1>
      </div>

      {/* Customer Information */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ margin: '0 0 1.5rem 0' }}>Customer Information</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <strong>BigCommerce ID:</strong>
            <p style={{ margin: '0.5rem 0', fontFamily: 'monospace' }}>{customer.id || 'N/A'}</p>
          </div>
          <div>
            <strong>Vendor ID:</strong>
            <p style={{ margin: '0.5rem 0', fontFamily: 'monospace' }}>
              {customer.vendor_id ? customer.vendor_id : 'N/A'}
            </p>
          </div>
          <div>
            <strong>Name:</strong>
            <p style={{ margin: '0.5rem 0' }}>{getCustomerName(customer)}</p>
          </div>
          <div>
            <strong>Email:</strong>
            <p style={{ margin: '0.5rem 0' }}>{customer.email || 'N/A'}</p>
          </div>
          <div>
            <strong>Phone:</strong>
            <p style={{ margin: '0.5rem 0' }}>{customer.phone || 'N/A'}</p>
          </div>
          {customer.company && (
            <div>
              <strong>Company:</strong>
              <p style={{ margin: '0.5rem 0' }}>{customer.company}</p>
            </div>
          )}
          <div>
            <strong>Customer Group:</strong>
            <p style={{ margin: '0.5rem 0' }}>
              {customer.customer_group_id ? (
                <>
                  {customerGroupName || `Group ${customer.customer_group_id}`}
                  <span style={{ marginLeft: '0.5rem', color: '#999', fontSize: '0.9rem', fontFamily: 'monospace' }}>
                    (ID: {customer.customer_group_id})
                  </span>
                </>
              ) : (
                'N/A'
              )}
            </p>
          </div>
          <div>
            <strong>Date Created:</strong>
            <p style={{ margin: '0.5rem 0' }}>{formatDate(customer.date_created)}</p>
          </div>
        </div>
      </div>

      <div
        className="customer-addresses-card"
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ margin: '0 0 1rem 0' }}>Addresses</h2>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Current and previous addresses on file:{' '}
          <strong>{Array.isArray(customer.addresses) ? customer.addresses.length : 0}</strong>
        </p>
        {Array.isArray(customer.addresses) && customer.addresses.length > 0 ? (
          <div className="customer-addresses-grid">
            {customer.addresses.map(
              (
                address: {
                  id?: number;
                  first_name?: string;
                  last_name?: string;
                  company?: string;
                  street_1?: string;
                  street_2?: string;
                  city?: string;
                  state?: string;
                  zip?: string;
                  country?: string;
                  phone?: string;
                  address_type?: string;
                },
                index: number,
              ) => {
                const lines = formatCustomerAddressLines(address);
                return (
                  <div
                    key={address.id ?? `address-${index}`}
                    className="customer-address-card"
                    style={{
                      padding: '1rem',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '4px',
                      border: '1px solid #eee',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '0.35rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <strong>Address {index + 1}</strong>
                      {address.address_type ? (
                        <span style={{ color: '#666', fontSize: '0.85rem' }}>
                          - {formatAddressType(address.address_type)}
                        </span>
                      ) : null}
                    </div>
                    {lines.length > 0 ? (
                      lines.map((line, lineIndex) => (
                        <p key={`${address.id ?? index}-${lineIndex}`} style={{ margin: '0.25rem 0', color: '#333' }}>
                          {line}
                        </p>
                      ))
                    ) : (
                      <p style={{ margin: 0, color: '#666' }}>No address details available.</p>
                    )}
                  </div>
                );
              },
            )}
          </div>
        ) : (
          <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666' }}>
            No addresses on file for this customer.
          </div>
        )}
      </div>

      {/* Customer Groups Section */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ margin: '0 0 1rem 0' }}>Customer Groups</h2>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          This customer belongs to <strong>{customer.customer_groups_count || 0}</strong> customer group(s).
        </p>

        {customer.customer_groups && customer.customer_groups.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {customer.customer_groups.map((group: any) => (
              <div
                key={group.id}
                onClick={() => navigate(`/customer-groups/${group.id}`)}
                style={{
                  padding: '1rem',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '4px',
                  border: '1px solid #eee',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{group.name || `Group #${group.id}`}</strong>
                    {group.is_default && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '3px',
                          backgroundColor: '#2ecc71',
                          color: '#fff',
                          fontSize: '0.75rem',
                        }}
                      >
                        Default
                      </span>
                    )}
                  </div>
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>→</span>
                </div>
                {group.description && (
                  <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                    {group.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666' }}>
            This customer is not assigned to any customer groups.
          </div>
        )}
      </div>

      {/* Orders Section */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ margin: '0 0 1rem 0' }}>Orders</h2>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Recent orders for this customer: <strong>{customer.orders_pagination?.total ?? customer.orders?.length ?? 0}</strong>
        </p>

        {customer.orders && customer.orders.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Order ID</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Total</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {customer.orders.map((order: { id: number; status?: string; total_inc_tax?: string; date_created?: string }) => (
                  <tr key={order.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '0.75rem', fontFamily: 'monospace' }}>
                      <Link
                        to={`/orders/${order.id}`}
                        state={orderReturnState}
                        style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                      >
                        #{order.id}
                      </Link>
                    </td>
                    <td style={{ padding: '0.75rem' }}>{order.status || 'N/A'}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {order.total_inc_tax ? formatCurrency(parseFloat(order.total_inc_tax)) : 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{order.date_created ? formatDate(order.date_created) : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666' }}>
            No orders found for this customer.
          </div>
        )}
      </div>

      {/* Promotions Section */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '1.5rem',
        }}
      >
        <h2 style={{ margin: '0 0 1rem 0' }}>Promotions</h2>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Promotions linked to this customer's groups: <strong>{customer.promotions_count || 0}</strong>
        </p>

        {customer.promotions && customer.promotions.length > 0 ? (
          <div
            style={{
              overflowX: 'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem',
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Type
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Discount
                  </th>
                  <th
                    style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#495057',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {customer.promotions.map((promo: any) => (
                  <tr
                    key={promo.id}
                    onClick={() => navigate(`/promotions/${promo.id}`)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid #dee2e6',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }}
                  >
                    <td style={{ padding: '0.75rem', color: '#2c3e50', fontWeight: 500 }}>
                      {promo.name || `Promotion #${promo.id}`}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#666' }}>
                      {getPromotionType(promo)}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#666' }}>
                      {promo.type === 'percentage_discount' && `${promo.amount || 0}%`}
                      {promo.type === 'fixed_discount' && formatCurrency(promo.amount || 0)}
                      {promo.type === 'shipping_discount' && 'Free Shipping'}
                      {!promo.type && 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '3px',
                          backgroundColor: isPromotionActive(promo) ? '#2ecc71' : '#95a5a6',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                        }}
                      >
                        {isPromotionActive(promo) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666' }}>
            No promotions are linked to this customer's groups.
          </div>
        )}
      </div>
    </div>
  );
}

